/**
 * Unit Conversion System for Grocery List Matching
 *
 * Handles conversion between different measurement systems:
 * - Volume: cups, ml, liters, fl oz, tbsp, tsp
 * - Weight: g, kg, oz, lb
 * - Count: pieces, cloves, items (no conversion)
 */

export type UnitType = 'volume' | 'weight' | 'count' | 'unknown';

/**
 * Volume conversion factors (all converted to milliliters)
 */
const VOLUME_TO_ML: Record<string, number> = {
  // Metric
  'ml': 1,
  'milliliter': 1,
  'milliliters': 1,
  'l': 1000,
  'liter': 1000,
  'liters': 1000,

  // Imperial
  'cup': 240,
  'cups': 240,
  'c': 240,
  'tablespoon': 15,
  'tablespoons': 15,
  'tbsp': 15,
  'tbs': 15,
  'teaspoon': 5,
  'teaspoons': 5,
  'tsp': 5,
  'fluid ounce': 30,
  'fluid ounces': 30,
  'fl oz': 30,
  'fl. oz': 30,
  'floz': 30,

  // French units
  'à soupe': 15,  // tablespoon
  'à café': 5,    // teaspoon
  'c. à soupe': 15,
  'c. à café': 5,
  'tasse': 240,   // cup
  'tasses': 240,
};

/**
 * Weight conversion factors (all converted to grams)
 */
const WEIGHT_TO_G: Record<string, number> = {
  // Metric
  'g': 1,
  'gram': 1,
  'grams': 1,
  'gramme': 1,
  'grammes': 1,
  'kg': 1000,
  'kilogram': 1000,
  'kilograms': 1000,
  'kilogramme': 1000,
  'kilogrammes': 1000,

  // Imperial
  'oz': 28.3495,
  'ounce': 28.3495,
  'ounces': 28.3495,
  'lb': 453.592,
  'lbs': 453.592,
  'pound': 453.592,
  'pounds': 453.592,
  'livre': 453.592,  // French pound
  'livres': 453.592,
};

/**
 * Count-based units (no conversion needed)
 */
const COUNT_UNITS = [
  'piece', 'pieces', 'item', 'items', 'whole', 'unit', 'units',
  'clove', 'cloves', 'head', 'heads', 'bunch', 'bunches',
  'stalk', 'stalks', 'slice', 'slices', 'leaf', 'leaves',
  'can', 'cans', 'jar', 'jars', 'package', 'packages', 'pkg',
  'bag', 'bags', 'box', 'boxes', 'bottle', 'bottles',
  // Empty string means count-based
  '',
];

/**
 * Normalize unit string to canonical form
 * Handles variations, plurals, abbreviations
 */
export function normalizeUnit(unit: string | undefined): string {
  if (!unit) return '';

  const normalized = unit.toLowerCase().trim()
    .replace(/\.$/, '') // Remove trailing period
    .replace(/\s+/g, ' '); // Normalize whitespace

  return normalized;
}

/**
 * Determine the type of a unit
 */
export function getUnitType(unit: string | undefined): UnitType {
  const normalized = normalizeUnit(unit);

  if (!normalized || COUNT_UNITS.includes(normalized)) {
    return 'count';
  }

  if (VOLUME_TO_ML[normalized] !== undefined) {
    return 'volume';
  }

  if (WEIGHT_TO_G[normalized] !== undefined) {
    return 'weight';
  }

  return 'unknown';
}

/**
 * Check if two units are compatible for conversion
 */
export function areUnitsCompatible(unit1: string | undefined, unit2: string | undefined): boolean {
  const type1 = getUnitType(unit1);
  const type2 = getUnitType(unit2);

  // Unknown units are incompatible
  if (type1 === 'unknown' || type2 === 'unknown') {
    return false;
  }

  // Count units are only compatible with other count units
  if (type1 === 'count' || type2 === 'count') {
    return type1 === type2;
  }

  // Volume and weight must match
  return type1 === type2;
}

/**
 * Convert amount to base unit (ml for volume, g for weight)
 * Returns null if unit is invalid or not convertible
 */
export function convertToBaseUnit(
  amount: number,
  unit: string | undefined,
  type: 'volume' | 'weight'
): number | null {
  const normalized = normalizeUnit(unit);

  if (type === 'volume') {
    const factor = VOLUME_TO_ML[normalized];
    if (factor !== undefined) {
      return amount * factor;
    }
  } else if (type === 'weight') {
    const factor = WEIGHT_TO_G[normalized];
    if (factor !== undefined) {
      return amount * factor;
    }
  }

  return null;
}

/**
 * Convert from base unit back to target unit
 */
export function convertFromBaseUnit(
  baseAmount: number,
  targetUnit: string | undefined,
  type: 'volume' | 'weight'
): number | null {
  const normalized = normalizeUnit(targetUnit);

  if (type === 'volume') {
    const factor = VOLUME_TO_ML[normalized];
    if (factor !== undefined) {
      return baseAmount / factor;
    }
  } else if (type === 'weight') {
    const factor = WEIGHT_TO_G[normalized];
    if (factor !== undefined) {
      return baseAmount / factor;
    }
  }

  return null;
}

/**
 * Convert between any two compatible units
 * Returns null if units are incompatible or conversion fails
 *
 * @example
 * convertBetweenUnits(1, 'cup', 'ml') // 240
 * convertBetweenUnits(16, 'oz', 'lb') // 1
 * convertBetweenUnits(2, 'cloves', 'pieces') // 2 (count units pass through)
 */
export function convertBetweenUnits(
  amount: number,
  fromUnit: string | undefined,
  toUnit: string | undefined
): number | null {
  // Normalize inputs
  const from = normalizeUnit(fromUnit);
  const to = normalizeUnit(toUnit);

  // If units are the same, no conversion needed
  if (from === to) {
    return amount;
  }

  // Check compatibility
  if (!areUnitsCompatible(from, to)) {
    return null;
  }

  const type = getUnitType(from);

  // Count units don't need conversion (just pass through)
  if (type === 'count') {
    return amount;
  }

  // Convert volume or weight
  if (type === 'volume' || type === 'weight') {
    // Step 1: Convert to base unit
    const baseAmount = convertToBaseUnit(amount, from, type);
    if (baseAmount === null) {
      return null;
    }

    // Step 2: Convert from base unit to target unit
    const result = convertFromBaseUnit(baseAmount, to, type);
    return result;
  }

  return null;
}

/**
 * Format a unit conversion result for display
 *
 * @example
 * formatConversion(240, 'ml', 'cup') // "1 cup (240 ml)"
 */
export function formatConversion(
  amount: number,
  unit: string,
  originalAmount?: number,
  originalUnit?: string
): string {
  const rounded = amount % 1 === 0 ? amount.toFixed(0) : amount.toFixed(1);

  if (originalAmount && originalUnit) {
    return `${rounded} ${unit} (from ${originalAmount} ${originalUnit})`;
  }

  return `${rounded} ${unit}`;
}

/**
 * Get list of compatible units for a given unit
 * Returns all units that can be converted to/from the given unit
 *
 * Note: For condiments and semi-liquids, we show BOTH volume and weight units
 * even though direct g↔ml conversions aren't supported (would need density data)
 *
 * @example
 * getCompatibleUnits('cup') // ['ml', 'liter', 'tablespoon', 'teaspoon', 'fl oz', ...]
 * getCompatibleUnitsForIngredient('cup', 'mustard') // [...volume..., ...weight...] (hybrid)
 */
export function getCompatibleUnits(unit: string | undefined): string[] {
  const type = getUnitType(unit);

  if (type === 'volume') {
    return ['ml', 'liter', 'cup', 'tablespoon', 'teaspoon', 'fl oz'];
  }

  if (type === 'weight') {
    return ['g', 'kg', 'oz', 'lb'];
  }

  if (type === 'count') {
    return ['piece', 'bunch', 'head', 'clove'];
  }

  return [];
}

/**
 * Get compatible units for a specific ingredient
 * Special handling for:
 * - Condiments/semi-liquids: volume OR weight
 * - Count-based produce: context-specific count units
 *
 * @example
 * getCompatibleUnitsForIngredient('cup', 'mustard') // [...volume..., ...weight...]
 * getCompatibleUnitsForIngredient('piece', 'apple') // ['piece', 'g', 'oz']
 * getCompatibleUnitsForIngredient('clove', 'garlic') // ['clove', 'head', 'piece', 'g', 'oz']
 */
export function getCompatibleUnitsForIngredient(
  unit: string | undefined,
  ingredientName?: string
): string[] {
  const type = getUnitType(unit);
  const baseUnits = getCompatibleUnits(unit);

  if (!ingredientName) {
    return baseUnits;
  }

  const normalized = ingredientName.toLowerCase();

  // Handle condiments/semi-liquids (can be measured by volume OR weight)
  const condiments = [
    'mustard', 'dijon', 'ketchup', 'catsup', 'mayo', 'mayonnaise',
    'hot sauce', 'sriracha', 'peanut butter', 'almond butter', 'tahini',
    'jam', 'jelly', 'preserve', 'marmalade', 'honey', 'syrup',
  ];

  const isCondiment = condiments.some(c => normalized.includes(c));

  if (isCondiment) {
    // Return both volume and weight units
    return [
      ...['ml', 'liter', 'cup', 'tablespoon', 'teaspoon', 'fl oz'], // Volume
      ...['g', 'kg', 'oz', 'lb'], // Weight
    ];
  }

  // Handle produce items (regardless of current unit type)
  // This allows cauliflower at "500 g" to show count options like "head" and "piece"

  // Eggs - countable protein (sold by count: 6, 12, 18, etc.)
  if (normalized.includes('egg') && !normalized.includes('eggplant')) {
    return ['piece', 'g', 'oz', 'lb'];
  }

  // Garlic-specific units (fresh garlic, not powder)
  if (normalized.includes('garlic') && !normalized.includes('powder')) {
    return ['clove', 'head', 'piece', 'g', 'oz'];
  }

  // Head vegetables (lettuce, cabbage, broccoli, cauliflower)
  const headVegetables = ['lettuce', 'cabbage', 'broccoli', 'cauliflower', 'bok choy'];
  if (headVegetables.some(v => normalized.includes(v))) {
    return ['head', 'piece', 'g', 'oz', 'lb'];
  }

  // Bunch items (herbs, bananas, grapes, greens)
  const bunchItems = ['basil', 'parsley', 'cilantro', 'mint', 'thyme', 'rosemary',
                      'banana', 'grape', 'spinach', 'kale', 'chard'];
  if (bunchItems.some(b => normalized.includes(b))) {
    return ['bunch', 'piece', 'g', 'oz'];
  }

  // Common produce (tomatoes, cucumbers, peppers, onions, apples, etc.)
  const commonProduce = [
    'tomato', 'cucumber', 'pepper', 'bell pepper', 'onion', 'potato',
    'apple', 'banana', 'orange', 'avocado', 'lemon', 'lime',
    'carrot', 'zucchini', 'eggplant', 'squash', 'beet', 'turnip', 'radish'
  ];
  if (commonProduce.some(p => normalized.includes(p))) {
    return ['piece', 'g', 'oz', 'lb'];
  }

  // Handle count-based items that aren't produce
  if (type === 'count') {
    // Default count items: piece + weight options
    return ['piece', 'g', 'oz', 'lb'];
  }

  return baseUnits;
}

/**
 * Get quick unit suggestions for switching
 * Returns 3 most relevant alternative units with CROSS-TYPE suggestions for easy switching
 *
 * NEW: Shows one unit from each applicable measurement type (count, weight, volume)
 * instead of only showing units within the same type.
 *
 * @param currentUnit - The current unit
 * @param ingredientName - Optional ingredient name for context-aware suggestions
 *
 * @example
 * getQuickUnitSuggestions('g', 'tomato') // ['piece', 'kg', 'oz']
 * getQuickUnitSuggestions('ml', 'milk') // ['cup', 'liter', 'g']
 */
export function getQuickUnitSuggestions(
  currentUnit: string | undefined,
  ingredientName?: string
): string[] {
  const normalized = normalizeUnit(currentUnit);
  const type = getUnitType(normalized);

  // If no ingredient name provided, use old behavior for backward compatibility
  if (!ingredientName) {
    const oldSuggestions: Record<string, string[]> = {
      // Volume units
      'cup': ['ml', 'liter', 'tablespoon'],
      'cups': ['ml', 'liter', 'tablespoon'],
      'ml': ['cup', 'liter', 'tablespoon'],
      'milliliter': ['cup', 'liter', 'tablespoon'],
      'milliliters': ['cup', 'liter', 'tablespoon'],
      'liter': ['ml', 'cup', 'fl oz'],
      'liters': ['ml', 'cup', 'fl oz'],
      'l': ['ml', 'cup', 'fl oz'],
      'tablespoon': ['teaspoon', 'ml', 'cup'],
      'tablespoons': ['teaspoon', 'ml', 'cup'],
      'tbsp': ['teaspoon', 'ml', 'cup'],
      'teaspoon': ['tablespoon', 'ml', 'cup'],
      'teaspoons': ['tablespoon', 'ml', 'cup'],
      'tsp': ['tablespoon', 'ml', 'cup'],
      'fl oz': ['ml', 'cup', 'liter'],
      'floz': ['ml', 'cup', 'liter'],

      // Weight units
      'g': ['kg', 'oz', 'lb'],
      'gram': ['kg', 'oz', 'lb'],
      'grams': ['kg', 'oz', 'lb'],
      'kg': ['g', 'lb', 'oz'],
      'kilogram': ['g', 'lb', 'oz'],
      'kilograms': ['g', 'lb', 'oz'],
      'oz': ['g', 'lb', 'kg'],
      'ounce': ['g', 'lb', 'kg'],
      'ounces': ['g', 'lb', 'kg'],
      'lb': ['kg', 'oz', 'g'],
      'lbs': ['kg', 'oz', 'g'],
      'pound': ['kg', 'oz', 'g'],
      'pounds': ['kg', 'oz', 'g'],

      // Count units - limited options
      'piece': ['bunch', 'head'],
      'pieces': ['bunch', 'head'],
      'bunch': ['piece', 'head'],
      'bunches': ['piece', 'head'],
      'head': ['piece', 'bunch'],
      'heads': ['piece', 'bunch'],
      'clove': ['head', 'piece'],
      'cloves': ['head', 'piece'],
    };

    if (oldSuggestions[normalized]) {
      return oldSuggestions[normalized];
    }

    // Fallback: return some units from same category
    if (type === 'volume') {
      return ['ml', 'cup', 'liter'];
    }
    if (type === 'weight') {
      return ['g', 'oz', 'lb'];
    }
    if (type === 'count') {
      return ['piece'];
    }

    return [];
  }

  // NEW: Cross-type suggestions based on ingredient context
  const normalizedIngredient = ingredientName.toLowerCase();

  // Spices - small measurements (volume/weight)
  const spices = ['salt', 'pepper', 'garlic powder', 'onion powder', 'cinnamon', 'nutmeg',
                  'cumin', 'paprika', 'oregano', 'basil', 'thyme', 'rosemary'];
  if (spices.some(s => normalizedIngredient.includes(s) && !normalizedIngredient.includes('garlic') || normalizedIngredient.includes('powder'))) {
    return ['tablespoon', 'teaspoon', 'g'];
  }

  // Condiments - both volume and weight
  const condiments = ['mustard', 'ketchup', 'mayo', 'peanut butter', 'jam', 'honey', 'syrup'];
  if (condiments.some(c => normalizedIngredient.includes(c))) {
    return ['g', 'ml', 'tablespoon'];
  }

  // Liquids - volume first, then weight
  const liquids = ['milk', 'cream', 'oil', 'juice', 'broth', 'stock', 'water', 'wine', 'beer',
                   'vinegar', 'sauce', 'yogurt', 'buttermilk'];
  if (liquids.some(l => normalizedIngredient.includes(l))) {
    return ['ml', 'cup', 'g'];
  }

  // Eggs - count first
  if (normalizedIngredient.includes('egg') && !normalizedIngredient.includes('eggplant')) {
    return ['piece', 'g', 'kg'];
  }

  // Garlic - special count units
  if (normalizedIngredient.includes('garlic') && !normalizedIngredient.includes('powder')) {
    return ['clove', 'head', 'g'];
  }

  // Head vegetables - count first, then weight
  const headVegetables = ['lettuce', 'cabbage', 'broccoli', 'cauliflower', 'bok choy'];
  if (headVegetables.some(v => normalizedIngredient.includes(v))) {
    return ['head', 'piece', 'kg'];
  }

  // Bunch items - count first
  const bunchItems = ['basil', 'parsley', 'cilantro', 'mint', 'banana', 'grape', 'spinach', 'kale'];
  if (bunchItems.some(b => normalizedIngredient.includes(b))) {
    return ['bunch', 'piece', 'g'];
  }

  // Common produce - count first, then weights
  const produce = ['tomato', 'cucumber', 'pepper', 'onion', 'potato', 'apple', 'orange',
                   'avocado', 'lemon', 'lime', 'carrot', 'zucchini'];
  if (produce.some(p => normalizedIngredient.includes(p))) {
    return ['piece', 'g', 'kg'];
  }

  // Proteins - weight first, then count
  const proteins = ['chicken', 'beef', 'pork', 'fish', 'salmon', 'turkey', 'bacon', 'sausage'];
  if (proteins.some(p => normalizedIngredient.includes(p))) {
    return ['g', 'piece', 'kg'];
  }

  // Packaged items - count first, then weight
  const packaged = ['bar', 'snack', 'pack', 'can', 'jar', 'box', 'bottle'];
  if (packaged.some(p => normalizedIngredient.includes(p))) {
    return ['piece', 'g', 'oz'];
  }

  // Default: show cross-type options for maximum flexibility
  // Count + Weight + Volume
  return ['piece', 'g', 'ml'];
}

/**
 * Format conversion display for UI
 * Shows approximate conversion in a compact format
 *
 * @example
 * formatConversionDisplay(2, 'cup', 'ml') // "≈ 480ml"
 * formatConversionDisplay(500, 'g', 'lb') // "≈ 1.1lb"
 */
export function formatConversionDisplay(
  quantity: number,
  fromUnit: string | undefined,
  toUnit?: string | undefined
): string | null {
  const from = normalizeUnit(fromUnit);
  const type = getUnitType(from);

  // Auto-select best conversion unit if not provided
  let targetUnit = toUnit ? normalizeUnit(toUnit) : undefined;
  if (!targetUnit) {
    const defaultConversions: Record<string, string> = {
      // Volume defaults
      'cup': 'ml',
      'cups': 'ml',
      'ml': 'cup',
      'milliliter': 'cup',
      'milliliters': 'cup',
      'liter': 'ml',
      'liters': 'ml',
      'l': 'ml',
      'tablespoon': 'ml',
      'tablespoons': 'ml',
      'tbsp': 'ml',
      'teaspoon': 'ml',
      'teaspoons': 'ml',
      'tsp': 'ml',
      'fl oz': 'ml',
      'floz': 'ml',

      // Weight defaults
      'g': 'oz',
      'gram': 'oz',
      'grams': 'oz',
      'kg': 'lb',
      'kilogram': 'lb',
      'kilograms': 'lb',
      'oz': 'g',
      'ounce': 'g',
      'ounces': 'g',
      'lb': 'kg',
      'lbs': 'kg',
      'pound': 'kg',
      'pounds': 'kg',
    };

    targetUnit = defaultConversions[from];
    if (!targetUnit) return null; // No default conversion for this unit
  }

  const converted = convertBetweenUnits(quantity, from, targetUnit);
  if (converted === null) return null;

  // Smart rounding based on magnitude
  let rounded: string;
  if (converted >= 100) {
    rounded = Math.round(converted).toString();
  } else if (converted >= 10) {
    rounded = (Math.round(converted * 10) / 10).toString();
  } else {
    rounded = (Math.round(converted * 100) / 100).toString();
  }

  return `≈ ${rounded}${targetUnit}`;
}

/**
 * Common conversion reference data
 * Useful for showing conversion helpers to users
 */
export const COMMON_CONVERSIONS = {
  volume: [
    { from: '1 cup', to: '240ml' },
    { from: '1/2 cup', to: '120ml' },
    { from: '1/4 cup', to: '60ml' },
    { from: '1 tablespoon', to: '15ml' },
    { from: '1 teaspoon', to: '5ml' },
    { from: '1 liter', to: '4.2 cups' },
    { from: '1 fl oz', to: '30ml' },
  ],
  weight: [
    { from: '1 kg', to: '2.2 lb' },
    { from: '1 lb', to: '454g' },
    { from: '1 oz', to: '28g' },
    { from: '100g', to: '3.5 oz' },
  ],
};
